import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { PageHeader, Input, Labeled } from "./Rentals";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { PhoneField, PreferredContactMethodField } from "../components/PhoneField";
import { buildPhoneFieldValue, getPhoneFieldError, touchPhoneField } from "../lib/phone";

const WA = "3547883999";
const EMAIL = "info@icefleet.is";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: buildPhoneFieldValue(),
    preferred_contact_method: null,
    service_type: "general",
    preferred_dates: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const phoneError = getPhoneFieldError(form.phone);
    if (phoneError) {
      setForm({ ...form, phone: touchPhoneField(form.phone) });
      toast.error(phoneError);
      return;
    }
    setBusy(true);
    try {
      await api.post("/inquiries?kind=quote", {
        ...form,
        phone: form.phone.e164 || null,
        phone_country: form.phone.country || null,
      });
      toast.success("Thanks — we'll reply by email shortly.");
      setForm({ ...form, message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Contact" title="Get a quote or ask a question." subtitle="We reply fast — usually within a few hours during working days." />
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 grid gap-10 lg:grid-cols-5">
        <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-3 rounded-xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Labeled label="Name"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" /></Labeled>
            <Labeled label="Company (optional)"><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="contact-company" /></Labeled>
            <Labeled label="Email"><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" /></Labeled>
            <Labeled label="Phone / WhatsApp">
              <PhoneField
                value={form.phone}
                onChange={(phone) => setForm({ ...form, phone })}
                countryTestId="contact-phone-country"
                numberTestId="contact-phone"
                errorTestId="contact-phone-error"
              />
            </Labeled>
            <Labeled label="Preferred contact method">
              <PreferredContactMethodField
                value={form.preferred_contact_method}
                onChange={(preferred_contact_method) => setForm({ ...form, preferred_contact_method })}
                testid="contact-contact-method"
              />
            </Labeled>
            <Labeled label="Service">
              <select value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} data-testid="contact-service" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 outline-none">
                <option value="general">General question</option>
                <option value="rental">Van rental</option>
                <option value="transport">Airport / transfer</option>
                <option value="tours">Private tour</option>
                <option value="long_term">Long-term hire</option>
              </select>
            </Labeled>
            <Labeled label="Preferred dates"><Input placeholder="e.g. 14–20 July" value={form.preferred_dates} onChange={(e) => setForm({ ...form, preferred_dates: e.target.value })} data-testid="contact-dates" /></Labeled>
          </div>
          <Labeled label="Message">
            <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 outline-none" />
          </Labeled>
          <button type="submit" disabled={busy} data-testid="contact-submit" className="rounded-md bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
            {busy ? "Sending…" : "Send message"}
          </button>
        </form>

        <aside className="lg:col-span-2 space-y-6">
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-6 space-y-4">
            <ContactRow icon={<MessageCircle className="text-sky-700" size={18} />} label="WhatsApp" value={`+${WA}`} href={`https://wa.me/${WA}`} testid="contact-whatsapp-link" />
            <ContactRow icon={<Mail className="text-sky-700" size={18} />} label="Email" value={EMAIL} href={`mailto:${EMAIL}`} testid="contact-email-link" />
            <ContactRow icon={<Phone className="text-sky-700" size={18} />} label="Phone" value={`+${WA}`} href={`tel:+${WA}`} testid="contact-phone-link" />
            <ContactRow icon={<MapPin className="text-sky-700" size={18} />} label="Location" value="Krókháls 7, 110 Reykjavík, Iceland" />
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="contact-legal-entity-card">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-700">Legal entity</p>
            <div className="mt-4 space-y-2 text-sm text-slate-600">
              <p><span className="font-semibold text-slate-900">Company:</span> FONDO ehf.</p>
              <p><span className="font-semibold text-slate-900">Kennitala:</span> 521216-1310</p>
              <p><span className="font-semibold text-slate-900">Brand:</span> IceFleet</p>
              <p><span className="font-semibold text-slate-900">Address:</span> Krókháls 7, 110 Reykjavík, Iceland</p>
              <p><span className="font-semibold text-slate-900">Email:</span> {EMAIL}</p>
              <p><span className="font-semibold text-slate-900">Phone:</span> +{WA}</p>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 p-6">
            <p className="font-semibold text-slate-900">Need it fast?</p>
            <p className="mt-1 text-sm text-slate-600">
              For urgent requests, WhatsApp is the quickest way to reach us.
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
}

function ContactRow({ icon, label, value, href, testid }) {
  const content = (
    <div className="flex items-start gap-3">
      <div className="mt-0.5">{icon}</div>
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</p>
        <p className="text-slate-900 font-semibold">{value}</p>
      </div>
    </div>
  );
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid} className="block hover:opacity-80">
      {content}
    </a>
  ) : (
    content
  );
}
